<script lang="ts">
    import type {TabButton, TabId} from 'shared/ui-widgets/tabset/types';
    import {Tab, TabGroup, TabList} from '@rgossiaux/svelte-headlessui';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import KpTabGroupChangeListener from 'shared/ui-widgets/tabset/KpTabGroupChangeListener.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpTabButtonsActiveTabSelector from 'shared/ui-widgets/tabset/KpTabButtonsActiveTabSelector.svelte';

    export let tabButtons: TabButton[];
    export let tabToSelect: TabId | null = null;
    export let hrefGenerator: ((id: TabId) => string) | null = null;
    export let fillWidth = false;
</script>

<TabGroup class="kp-button-tabs {fillWidth ? 'fill-width' : ''}">
    <KpTabGroupChangeListener on:tab-change/>
    <KpTabButtonsActiveTabSelector {tabToSelect}/>

    <TabList class="kp-button-tabs-list">
        {#each tabButtons as tab(tab.id)}
            <Tab role="tab"
                 as="{isFunction(hrefGenerator) ? 'a' : 'button'}"
                 data-tab="{tab.id}"
                 disabled="{tab.disabled}"
                 class="{({selected}) => `kp-button-tabs-item no-decoration-anchor ${tab.id} ${selected ? 'selected' : ''}`}"
                 {...(isFunction(hrefGenerator) ? {href: hrefGenerator(tab.id)} : {})}>

                <IconedContent icon="{tab.icon}">
                    {tab.label}

                    {#if exists(tab.additionalContentComponent)}
                        <svelte:component this="{tab.additionalContentComponent.component}"
                                          {...tab.additionalContentComponent.props}/>
                    {/if}
                </IconedContent>
            </Tab>
        {/each}
    </TabList>
</TabGroup>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    :global {
        .kp-button-tabs {
            &.fill-width {
                width: 100%;

                .kp-button-tabs-list .kp-button-tabs-item {
                    flex: 1;
                }
            }

            .kp-button-tabs-list {
                display: flex;

                .kp-button-tabs-item {
                    padding: @spacing-s @spacing-m;
                    background-color: @themed-body-bg;
                    border: 1px solid @themed-border-default;
                    color: @themed-text-default;
                    white-space: nowrap;
                    outline: none;
                    cursor: pointer;
                    transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, opacity 0.2s ease-in-out, color 0.2s ease-in-out;

                    &.selected {
                        background-color: var(--selected-tab-highlight-color);
                        border: 1px solid rgba(0, 0, 0, 0.2);
                        color: white;
                    }

                    &:disabled {
                        opacity: 0.5;
                    }

                    &:first-child {
                        border-top-left-radius: @border-radius-default;
                        border-bottom-left-radius: @border-radius-default;
                    }

                    &:last-child {
                        border-top-right-radius: @border-radius-default;
                        border-bottom-right-radius: @border-radius-default;
                    }

                    &:not(:last-child) {
                        border-right: none;
                    }

                    &:enabled:not(.selected) {
                        &:hover {
                            background-color: @themed-panel-bg;
                        }
                    }
                }
            }
        }
    }
</style>